import { useCallback, useRef } from 'react';

import type { DebugInfo, MutationConfig, MutationError } from './useMutation.types';

export const useMutationHandler = (debug = false) => {
  const debugRef = useRef<DebugInfo[]>([]);

  const categorizeError = (error: any, result?: any): MutationError => {
    if (error?.name === 'AbortError' || error?.code === 'ECONNABORTED') {
      return {
        message: 'Request timed out',
        type: 'timeout',
        originalError: error,
      };
    }

    if (!navigator.onLine || error?.code === 'NETWORK_ERROR') {
      return {
        message: 'No internet connection',
        type: 'network',
        originalError: error,
      };
    }

    if (error?.graphQLErrors?.length > 0) {
      return {
        message: error.graphQLErrors[0]?.message || 'GraphQL error occurred',
        type: 'graphql',
        originalError: error,
        code: error.graphQLErrors[0]?.extensions?.code,
      };
    }

    if (result?.errors?.length > 0) {
      return {
        message: result.errors[0].message || 'GraphQL error occurred',
        type: 'graphql',
        originalError: error,
        code: result.errors[0].extensions?.code,
      };
    }

    if (error?.status >= 400 && error?.status < 500) {
      return {
        message: error.message || 'Validation error',
        type: 'validation',
        originalError: error,
        statusCode: error.status,
      };
    }

    return {
      message: error instanceof Error ? error.message : 'An unknown error occurred',
      type: 'unknown',
      originalError: error,
    };
  };

  const executeWithTimeout = async <T>(promise: Promise<T>, timeoutMs?: number): Promise<T> => {
    if (!timeoutMs) return promise;

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), timeoutMs),
    );

    return Promise.race([promise, timeoutPromise]);
  };

  const log = useCallback(
    (message: string, data?: any) => {
      if (debug) {
        console.group(`🔄 Mutation Handler: ${message}`);

        if (data) console.log(data);
        console.groupEnd();
      }
    },
    [debug],
  );

  const handleMutation = useCallback(
    async <T>(config: MutationConfig<T>): Promise<void> => {
      const { mutation, onSuccess, onError, extractSuccess, extractError, timeout, debugLabel = 'unnamed' } = config;

      const startTime = Date.now();
      let lastError: MutationError | null = null;

      log(`Starting mutation: ${debugLabel}`);

      try {
        log(`Executing mutation`);

        const result = await executeWithTimeout(mutation(), timeout);
        const isSuccess = extractSuccess ? extractSuccess(result) : !!result;

        if (isSuccess) {
          const duration = Date.now() - startTime;
          log(`✅ Success after ${duration}ms`, { result });

          debugRef.current.push({
            timestamp: startTime,
            label: debugLabel,
            duration,
          });

          if (onSuccess) {
            await onSuccess(result);
          }

          return;
        }

        if (extractError) {
          const errorMessage = extractError(result);

          if (errorMessage != null) {
            lastError = {
              message: errorMessage,
              type: 'graphql',
            };
            throw new Error(errorMessage);
          }
        }

        lastError = {
          message: 'Mutation failed with unknown error',
          type: 'unknown',
        };
        throw new Error('Mutation failed');
      } catch (error) {
        lastError = categorizeError(error);
        log(`❌ Mutation failed`, lastError);
      }

      if (lastError) {
        const duration = Date.now() - startTime;

        debugRef.current.push({
          timestamp: startTime,
          label: debugLabel,
          duration,
          error: lastError,
        });

        log(`💥 Final failure after ${duration}ms`, lastError);

        if (onError) {
          onError(lastError);
        }
      }
    },
    [log],
  );

  const getDebugInfo = useCallback(() => {
    return debug ? debugRef.current : [];
  }, [debug]);

  const clearDebugInfo = useCallback(() => {
    if (debug) {
      debugRef.current = [];
    }
  }, [debug]);

  return {
    handleMutation,
    getDebugInfo,
    clearDebugInfo,
  };
};
